import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brandmark, Crest } from "@/components/Brand";
import { CheckCircle2, ChevronLeft } from "lucide-react";

const PHOTOS = [
  "https://images.unsplash.com/photo-1628950752032-06fed9c9d0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1691357261752-a8f551ade626?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1633368098496-be7b5571547c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

const CHECKLIST = [
  ["Exterior", [["Facade & walls", "Good"], ["Roof & gutters", "Good"], ["Windows & shutters", "Good"], ["Exterior doors & locks", "Good"], ["Exterior lighting", "Good"]]],
  ["Garden & pool", [["Garden & plants", "Good"], ["Irrigation system", "Good"], ["Pool water & chemistry", "Needs attention", "pH slightly high, dosed and rebalanced"], ["Pool pump & equipment", "Good"]]],
  ["Interior", [["General cleanliness", "Good"], ["Walls & ceilings", "Good"], ["Floors", "Good"], ["Damp / mould", "Good"]]],
  ["Systems & safety", [["Electrics & fuse board", "Good"], ["Water & boiler", "Good"], ["Heating / A-C", "Good"], ["Wi-Fi & internet", "Good"], ["Alarm & security", "Good"], ["Smoke / CO detectors", "Good"]]],
  ["General", [["Post & deliveries", "Good"], ["Pest check", "Good"], ["Leaks & water damage", "Good"], ["Keys & access", "Good"]]],
];
const total = CHECKLIST.reduce((n, [, items]) => n + items.length, 0);
const flagged = CHECKLIST.reduce((n, [, items]) => n + items.filter((i) => i[1] === "Needs attention").length, 0);

const Sec = ({ label, value, accent }) => (
  <div><div className="lbl-m" style={{ color: "var(--muted)" }}>{label}</div><div className="font-serif-c" style={{ fontSize: 22, color: accent || "var(--ink)" }}>{value}</div></div>
);

export default function SampleReport() {
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div>
      <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(244,239,231,.94)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--line)" }}>
        <div className="wrapx" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 84 }}>
          <Link to="/" style={{ textDecoration: "none" }}><Brandmark tagline tagText="Property care · Mallorca" /></Link>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <button onClick={() => nav(-1)} data-testid="sample-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)" }}><ChevronLeft size={16} /> Back</button>
            <a href="/services" style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)", textDecoration: "none" }}>Services</a>
            <a href="/#book" className="btn-ink" style={{ textDecoration: "none" }} data-testid="sample-book">Book a call</a>
          </div>
        </div>
      </nav>

      <header className="wrapx" style={{ padding: "clamp(48px,7vw,90px) clamp(24px,5vw,52px) clamp(28px,4vw,40px)", textAlign: "center", maxWidth: 780 }}>
        <span className="lbl">A real example</span>
        <h1 style={{ fontSize: "clamp(36px,6vw,68px)", margin: "20px auto 0", maxWidth: "16ch" }}>This is what lands in your inbox.</h1>
        <p style={{ fontSize: 18, color: "var(--muted)", margin: "24px auto 0", maxWidth: "52ch", lineHeight: 1.75 }}>After every single visit, you receive a photographed report like the one below, so you always know the exact condition of your home. This is a sample.</p>
      </header>

      <div className="wrapx" style={{ maxWidth: 720, paddingBottom: "clamp(48px,7vw,90px)" }}>
        <div className="card-soft" style={{ padding: "clamp(20px,4vw,34px)" }} data-testid="sample-report-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <span className="lbl-m" style={{ color: "var(--olive)" }}>Visit report</span>
              <h2 style={{ fontSize: 28, margin: "4px 0" }}>Weekly inspection, 12 June</h2>
              <span className="pill-tag" style={{ background: "#e7ecdf", color: "#5B6440" }}>All good</span>
            </div>
            <Crest size={44} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginTop: 18, background: "var(--alt)", border: "1px solid var(--line)", borderRadius: 4, padding: 16 }}>
            <Sec label="Condition" value="Excellent" />
            <Sec label="Checkpoints" value={`${total - flagged} of ${total}`} />
            <Sec label="Flagged" value={flagged || "None"} accent={flagged ? "#8a5a1f" : undefined} />
            <Sec label="Next visit" value="19 June" />
          </div>
          <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>On site 09:30 to 10:45 · Inspected by the Solvana team</p>

          <p style={{ fontSize: 15, marginTop: 16, lineHeight: 1.7 }}>The house is in excellent order. Interior clean and dry with no signs of damp. Pool is healthy, pH was slightly high, so we dosed and rebalanced it on site. Garden and irrigation running well. All doors, shutters and the alarm checked and secured on leaving.</p>

          <div style={{ marginTop: 16 }}><span className="lbl-m">Meter readings</span>
            <div style={{ display: "flex", gap: 20, marginTop: 6, flexWrap: "wrap", fontSize: 14 }}>
              <span>Electricity: <b>44,210 kWh</b></span><span>Water: <b>1,832 m³</b></span><span>Gas: <b>318 m³</b></span>
            </div>
          </div>

          <div style={{ marginTop: 16 }}><span className="lbl-m">Security &amp; keyholding</span>
            <div style={{ display: "grid", gap: 5, marginTop: 6, fontSize: 14 }}>
              {["Alarm set on leaving", "All doors & windows secured", "Keys returned to coded safe"].map((l) => (
                <div key={l} style={{ display: "flex", alignItems: "center", gap: 8 }}><CheckCircle2 size={15} color="#5B6440" /> <span>{l}</span></div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 16 }}><span className="lbl-m">Photographs</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 8 }}>
              {PHOTOS.map((u, i) => <img key={i} src={u} alt="Villa inspection" style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 3 }} />)}
            </div>
          </div>

          <div style={{ marginTop: 16 }}><span className="lbl-m">Full inspection checklist ({total} checkpoints)</span>
            {CHECKLIST.map(([g, items]) => (
              <div key={g} style={{ marginTop: 10 }}>
                <div className="lbl-m" style={{ color: "var(--honey)" }}>{g}</div>
                {items.map(([area, status, note], i) => { const ok = status === "Good"; return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                    <span>{area}{note ? <em style={{ color: "var(--muted)" }}>, {note}</em> : ""}</span>
                    <span className="pill-tag" style={{ background: ok ? "#e7ecdf" : "#f3e4cf", color: ok ? "#5B6440" : "#8a5a1f", flexShrink: 0 }}>{status}</span>
                  </div>
                ); })}
              </div>
            ))}
          </div>

          <p style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>Inspected by the Solvana team on 12 June 2026. Every checkpoint above was physically verified during this visit.</p>
        </div>

        <div style={{ textAlign: "center", marginTop: "clamp(36px,5vw,56px)" }}>
          <h2 style={{ fontSize: "clamp(26px,3vw,38px)" }}>Want this for your home?</h2>
          <p style={{ color: "var(--muted)", margin: "12px auto 24px", maxWidth: "42ch" }}>A twenty-minute call, no obligation.</p>
          <a href="/#book" className="btn-ink btn-olive" style={{ textDecoration: "none" }} data-testid="sample-cta">Book a call</a>
        </div>
      </div>
    </div>
  );
}
